import React, { useMemo } from "react";
import { Field, Input, Select } from "../../../design-system";
import { DAYS, DAY_LABEL, TIMEZONES, formatMinutesShort } from "./formatters";

const hhMmToMinutes = (str) => {
  if (!str || typeof str !== "string") return null;
  const m = str.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

export const buildEmptyConfig = (timezone = "Europe/Madrid") => ({
  timezone,
  days: DAYS.reduce((acc, d) => {
    const works = d !== "sat" && d !== "sun";
    acc[d] = works
      ? { works: true, start: "09:00", end: "18:00", breakMinutes: "60" }
      : { works: false, start: "09:00", end: "18:00", breakMinutes: "0" };
    return acc;
  }, {}),
});

const computeDayTotal = (day) => {
  if (!day.works) return 0;
  const start = hhMmToMinutes(day.start);
  const end = hhMmToMinutes(day.end);
  if (start == null || end == null) return null;
  if (end <= start) return null;
  const shift = end - start;
  const brk = Math.max(0, parseInt(day.breakMinutes || "0", 10) || 0);
  if (brk >= shift) return null;
  return shift - brk;
};

export const buildConfigPayload = (formConfig) => {
  const days = {};
  let weeklyMinutes = 0;
  for (const key of DAYS) {
    const d = formConfig.days[key];
    if (!d.works) {
      days[key] = { total_minutes: 0 };
      continue;
    }
    const total = computeDayTotal(d);
    if (total == null) {
      throw new Error(
        `Día ${DAY_LABEL[key]}: revisa horarios o pausa (la pausa debe ser menor que el turno y el fin posterior al inicio).`,
      );
    }
    const entry = { start: d.start, end: d.end, total_minutes: total };
    const brk = Math.max(0, parseInt(d.breakMinutes || "0", 10) || 0);
    if (brk > 0) entry.break = { minutes: brk };
    days[key] = entry;
    weeklyMinutes += total;
  }
  return {
    weekly_minutes: weeklyMinutes,
    config: { timezone: formConfig.timezone, days },
  };
};

const cellStyle = {
  padding: "8px 12px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  verticalAlign: "middle",
};
const headerStyle = {
  ...cellStyle,
  textAlign: "left",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
  background: "var(--kr-bg-subtle)",
};

const WorkScheduleEditor = ({ value, onChange }) => {
  const totals = useMemo(() => {
    const perDay = {};
    let weekly = 0;
    for (const key of DAYS) {
      const t = computeDayTotal(value.days[key]);
      perDay[key] = t;
      if (t != null) weekly += t;
    }
    return { perDay, weekly };
  }, [value]);

  const updateDay = (dayKey, patch) => {
    onChange({
      ...value,
      days: { ...value.days, [dayKey]: { ...value.days[dayKey], ...patch } },
    });
  };

  return (
    <>
      <div className="kr-grid kr-gap-4 kr-mb-3" style={{ gridTemplateColumns: "1fr 1fr", alignItems: "end" }}>
        <Field label="Zona horaria">
          <Select
            value={value.timezone}
            onChange={(e) => onChange({ ...value, timezone: e.target.value })}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </Select>
        </Field>
        <div className="kr-text-end">
          <span className="kr-text-muted kr-fs-12" style={{ marginRight: 8 }}>Total semanal:</span>
          <span className="kr-fw-semibold kr-mono">
            {formatMinutesShort(totals.weekly)} ({totals.weekly} min)
          </span>
        </div>
      </div>

      <div
        style={{
          border: "1px solid var(--kr-border)",
          borderRadius: "var(--kr-radius-md)",
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13 }}>
          <thead>
            <tr>
              <th style={headerStyle}>Día</th>
              <th style={{ ...headerStyle, width: 80 }}>Trabaja</th>
              <th style={{ ...headerStyle, width: 130 }}>Inicio</th>
              <th style={{ ...headerStyle, width: 130 }}>Fin</th>
              <th style={{ ...headerStyle, width: 130 }}>Pausa (min)</th>
              <th style={{ ...headerStyle, width: 110 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {DAYS.map((key) => {
              const d = value.days[key];
              const total = totals.perDay[key];
              const invalid = d.works && total == null;
              return (
                <tr key={key} style={invalid ? { background: "var(--kr-status-warning-bg)" } : undefined}>
                  <td style={{ ...cellStyle, fontWeight: 500, color: "var(--kr-fg-strong)" }}>
                    {DAY_LABEL[key]}
                  </td>
                  <td style={cellStyle}>
                    <input
                      type="checkbox"
                      checked={d.works}
                      onChange={(e) => updateDay(key, { works: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: "var(--kr-action)" }}
                    />
                  </td>
                  <td style={cellStyle}>
                    <Input
                      type="time"
                      value={d.start}
                      disabled={!d.works}
                      onChange={(e) => updateDay(key, { start: e.target.value })}
                    />
                  </td>
                  <td style={cellStyle}>
                    <Input
                      type="time"
                      value={d.end}
                      disabled={!d.works}
                      onChange={(e) => updateDay(key, { end: e.target.value })}
                    />
                  </td>
                  <td style={cellStyle}>
                    <Input
                      type="number"
                      min={0}
                      value={d.breakMinutes}
                      disabled={!d.works}
                      onChange={(e) => updateDay(key, { breakMinutes: e.target.value })}
                    />
                  </td>
                  <td style={cellStyle}>
                    {!d.works ? (
                      <span className="kr-text-muted">—</span>
                    ) : total == null ? (
                      <span className="kr-text-danger kr-fs-11">inválido</span>
                    ) : (
                      <span className="kr-mono">{formatMinutesShort(total)}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default WorkScheduleEditor;
